<template>
  <section id="features" class="section-block">
    <div class="section-container">
      <span v-reveal class="section-label">Features</span>
      <h2 v-reveal class="section-title">核心功能</h2>
      <p v-reveal class="section-subtitle reveal--delay-1 section-subtitle--center">
        整合 OMS、WMS、ERP、DRP、BI 等关键能力，可在管理后台配置配图与文案。
      </p>
    <div
      v-for="(item, index) in coreFeatures"
      :key="item.id || item.title"
      v-reveal
      :class="['feature-block', 'reveal--delay-' + (index % 3 + 1)]"
    >
      <a-row type="flex" align="middle" :gutter="[28, 36]">
        <a-col :xs="24" :md="12" :order="index % 2 === 0 ? 1 : 2">
          <div class="feature-copy">
            <h3 class="feature-title">{{ item.title }}</h3>
            <p class="feature-subtitle">{{ item.subtitle }}</p>
            <p class="feature-detail">{{ item.detail }}</p>
          </div>
        </a-col>
        <a-col :xs="24" :md="12" :order="index % 2 === 0 ? 2 : 1">
          <div class="feature-image-wrap lift-hover">
            <div v-if="isSystemFeature(item)" class="product-stack-visual">
              <div class="product-stack">
                <div
                  v-for="(slide, slideIndex) in productSlides"
                  :key="slide.id || slideIndex"
                  :class="[
                    'product-stack__card',
                    'product-stack__card--' + getSlidePosition(slideIndex),
                    { 'product-stack__card--active': getSlidePosition(slideIndex) === 'active' }
                  ]"
                >
                  <img :src="slide.image" :alt="slide.name" class="product-stack__img" />
                  <span class="product-stack__label">{{ slide.name }}</span>
                </div>
              </div>
              <div class="product-stack-visual__dots">
                <span
                  v-for="(itemSlide, idx) in productSlides"
                  :key="itemSlide.id || idx"
                  :class="['stack-dot', { 'stack-dot--active': idx === activeProductIndex }]"
                />
              </div>
            </div>
            <div v-else-if="isMarketplaceFeature(item)" class="market-visual">
              <div class="market-visual__header">
                <span class="market-visual__eyebrow">GLOBAL CHANNELS</span>
                <h4 class="market-visual__title">多平台多国家协同</h4>
              </div>
              <div class="market-visual__platforms">
                <span class="platform-tag platform-tag--lazada">Lazada</span>
                <span class="platform-tag platform-tag--tiktok">TikTok Shop</span>
                <span class="platform-tag platform-tag--shopee">Shopee</span>
              </div>
              <div class="market-visual__flags" aria-label="支持多个国家站点">
                <span class="flag-item" title="美国">
                  <img
                    class="flag-icon"
                    src="https://flagcdn.com/w40/us.png"
                    alt="美国国旗"
                    loading="lazy"
                  />
                </span>
                <span class="flag-item" title="英国">
                  <img
                    class="flag-icon"
                    src="https://flagcdn.com/w40/gb.png"
                    alt="英国国旗"
                    loading="lazy"
                  />
                </span>
                <span class="flag-item" title="德国">
                  <img
                    class="flag-icon"
                    src="https://flagcdn.com/w40/de.png"
                    alt="德国国旗"
                    loading="lazy"
                  />
                </span>
                <span class="flag-item" title="法国">
                  <img
                    class="flag-icon"
                    src="https://flagcdn.com/w40/fr.png"
                    alt="法国国旗"
                    loading="lazy"
                  />
                </span>
                <span class="flag-item" title="日本">
                  <img
                    class="flag-icon"
                    src="https://flagcdn.com/w40/jp.png"
                    alt="日本国旗"
                    loading="lazy"
                  />
                </span>
                <span class="flag-item" title="新加坡">
                  <img
                    class="flag-icon"
                    src="https://flagcdn.com/w40/sg.png"
                    alt="新加坡国旗"
                    loading="lazy"
                  />
                </span>
                <span class="flag-item" title="马来西亚">
                  <img
                    class="flag-icon"
                    src="https://flagcdn.com/w40/my.png"
                    alt="马来西亚国旗"
                    loading="lazy"
                  />
                </span>
                <span class="flag-item" title="泰国">
                  <img
                    class="flag-icon"
                    src="https://flagcdn.com/w40/th.png"
                    alt="泰国国旗"
                    loading="lazy"
                  />
                </span>
              </div>
              <div class="market-visual__caption">统一管理多店铺、多渠道订单与库存</div>
            </div>
            <img v-else :src="resolveFeatureImage(item)" :alt="item.title" class="feature-image" />
          </div>
        </a-col>
      </a-row>
    </div>
    </div>
  </section>
</template>

<script>
const PRODUCT_HOME_IMAGE_MAP = {
  "prd-oms": require("@/static/oms/home.png"),
  "prd-wms": require("@/static/wms/home.png"),
  "prd-hwc": require("@/static/hwc/home.png"),
  "prd-drp": require("@/static/drp/home.png"),
  "prd-bi": require("@/static/bi/home.png"),
  "prd-tms": require("@/static/tms/home.png"),
  "prd-suyuan": require("@/static/suyuan/home.png"),
  "prd-pingce": require("@/static/pingce/home.png"),
  "prd-chat": require("@/static/chat/home.png")
};
const CORE_FEATURE_IMAGE_MAP = {
  "cf-4": require("@/static/home/bi.png")
};

export default {
  name: "CoreFeaturesSection",
  props: {
    coreFeatures: {
      type: Array,
      default: () => []
    },
    products: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      activeProductIndex: 0,
      productTimer: null
    };
  },
  computed: {
    productSlides() {
      const rows = Array.isArray(this.products) ? this.products : [];
      return rows
        .filter((item) => item && (PRODUCT_HOME_IMAGE_MAP[item.id] || item.image))
        .map((item, index) => ({
          id: item.id || "product-slide-" + index,
          name: item.name || "产品 " + (index + 1),
          image: PRODUCT_HOME_IMAGE_MAP[item.id] || item.image
        }));
    }
  },
  watch: {
    productSlides: {
      immediate: true,
      handler(nextSlides) {
        if (!nextSlides.length) {
          this.clearProductTimer();
          this.activeProductIndex = 0;
          return;
        }
        if (this.activeProductIndex >= nextSlides.length) {
          this.activeProductIndex = 0;
        }
        this.startProductTimer();
      }
    }
  },
  beforeDestroy() {
    this.clearProductTimer();
  },
  methods: {
    isSystemFeature(item) {
      const id = String(item && item.id ? item.id : "").toLowerCase();
      const title = String(item && item.title ? item.title : "");
      return id === "cf-1" || title.includes("全链路系统打通");
    },
    isMarketplaceFeature(item) {
      const id = String(item && item.id ? item.id : "").toLowerCase();
      const title = String(item && item.title ? item.title : "");
      return id === "cf-2" || title.includes("多平台多渠道");
    },
    getSlidePosition(index) {
      const total = this.productSlides.length;
      if (!total) return "hidden";
      const active = this.activeProductIndex % total;
      const prev = (active - 1 + total) % total;
      const next = (active + 1) % total;
      if (index === active) return "active";
      if (index === prev) return "prev";
      if (index === next) return "next";
      return "hidden";
    },
    resolveFeatureImage(item) {
      const id = String(item && item.id ? item.id : "").toLowerCase();
      if (CORE_FEATURE_IMAGE_MAP[id]) return CORE_FEATURE_IMAGE_MAP[id];
      const title = String(item && item.title ? item.title : "");
      if (title.includes("数据驱动决策")) return CORE_FEATURE_IMAGE_MAP["cf-4"];
      return item && item.image ? item.image : "";
    },
    startProductTimer() {
      this.clearProductTimer();
      if (this.productSlides.length <= 1) {
        return;
      }
      this.productTimer = window.setInterval(() => {
        this.activeProductIndex = (this.activeProductIndex + 1) % this.productSlides.length;
      }, 2200);
    },
    clearProductTimer() {
      if (this.productTimer) {
        window.clearInterval(this.productTimer);
        this.productTimer = null;
      }
    }
  }
};
</script>

<style scoped>
.section-subtitle--center {
  margin-left: auto;
  margin-right: auto;
  text-align: center;
}

.feature-block {
  padding: 56px 0;
  border-bottom: 1px solid var(--border);
}

.feature-block:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

.feature-block:first-of-type {
  padding-top: 0;
}

.feature-copy {
  padding: 12px 8px;
}

.feature-title {
  margin: 0 0 10px;
  font-size: 1.5rem;
  line-height: 1.3;
  color: var(--text);
  font-weight: 600;
}

.feature-subtitle {
  margin: 0 0 12px;
  font-size: 0.95rem;
  color: var(--primary);
  font-weight: 600;
}

.feature-detail {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.85;
  color: var(--text-muted);
}

.feature-image-wrap {
  width: 100%;
  max-width: 420px;
  margin-left: auto;
  margin-right: auto;
  aspect-ratio: 1;
  border-radius: var(--radius);
  overflow: hidden;
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
  background: var(--bg-soft);
  transition: transform 0.4s, box-shadow 0.4s, border-color 0.4s;
}

.feature-image-wrap:hover {
  transform: translateY(-4px);
  border-color: rgba(22, 119, 255, 0.25);
  box-shadow: 0 20px 48px rgba(22, 119, 255, 0.12);
}

.feature-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-stack-visual {
  position: relative;
  width: 100%;
  height: 100%;
  background: transparent;
}

.product-stack {
  position: relative;
  width: 100%;
  max-width: none;
  height: calc(100% - 30px);
  margin: 0;
}

.product-stack__card {
  position: absolute;
  left: 50%;
  top: 50%;
  width: min(82%, 360px);
  aspect-ratio: 16 / 11;
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #dce8ff;
  box-shadow: 0 10px 20px rgba(19, 66, 145, 0.16);
  transition:
    transform 0.72s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.72s cubic-bezier(0.22, 1, 0.36, 1),
    filter 0.72s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform, opacity, filter;
  backface-visibility: hidden;
}

.product-stack__card--active {
  transform: translate(-50%, -50%) scale(1);
  z-index: 3;
  opacity: 1;
  filter: none;
}

.product-stack__card--prev {
  transform: translate(calc(-50% - 19%), calc(-50% + 10px)) scale(0.9);
  z-index: 2;
  opacity: 0.7;
  filter: saturate(0.9) blur(0.2px);
}

.product-stack__card--next {
  transform: translate(calc(-50% + 19%), calc(-50% + 10px)) scale(0.9);
  z-index: 1;
  opacity: 0.7;
  filter: saturate(0.9) blur(0.2px);
}

.product-stack__card--hidden {
  transform: translate(-50%, -50%) scale(0.82);
  z-index: 0;
  opacity: 0;
  pointer-events: none;
  filter: saturate(0.9) blur(0.4px);
}

.product-stack__img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-stack__label {
  position: absolute;
  left: 10px;
  right: 10px;
  bottom: 10px;
  padding: 4px 8px;
  border-radius: 8px;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  background: linear-gradient(180deg, rgba(18, 35, 70, 0), rgba(18, 35, 70, 0.9));
}

.product-stack-visual__dots {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 10px;
  display: flex;
  justify-content: center;
  gap: 8px;
}

.stack-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #c6d8ff;
}

.stack-dot--active {
  width: 20px;
  background: linear-gradient(90deg, #1677ff, #4b8dff);
}

.market-visual {
  width: 100%;
  height: 100%;
  padding: 24px 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 18px;
  background:
    radial-gradient(circle at 15% 18%, rgba(83, 168, 255, 0.32), transparent 30%),
    radial-gradient(circle at 85% 84%, rgba(255, 120, 42, 0.28), transparent 36%),
    linear-gradient(140deg, #f5f9ff 0%, #eef4ff 48%, #f8f4ff 100%);
}

.market-visual__header {
  text-align: center;
}

.market-visual__eyebrow {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  background: rgba(22, 119, 255, 0.1);
  color: #2b5fb4;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
}

.market-visual__title {
  margin: 10px 0 0;
  font-size: 22px;
  line-height: 1.35;
  color: #0a275d;
}

.market-visual__platforms {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
}

.platform-tag {
  padding: 8px 12px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  color: #fff;
  box-shadow: 0 6px 16px rgba(34, 73, 151, 0.2);
}

.platform-tag--lazada {
  background: linear-gradient(135deg, #5b36f2, #9a5bff);
}

.platform-tag--tiktok {
  background: linear-gradient(135deg, #0f0f1a, #2b2b40);
}

.platform-tag--shopee {
  background: linear-gradient(135deg, #f97316, #fb923c);
}

.market-visual__flags {
  display: grid;
  grid-template-columns: repeat(4, minmax(48px, 1fr));
  gap: 10px;
  width: 100%;
  max-width: 290px;
  margin: 0 auto;
}

.flag-item {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 46px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #d9e6ff;
  box-shadow: 0 5px 14px rgba(30, 82, 178, 0.12);
  padding: 0 8px;
}

.flag-icon {
  display: block;
  width: 28px;
  height: 20px;
  border-radius: 3px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
  object-fit: cover;
}

.market-visual__caption {
  text-align: center;
  font-size: 13px;
  line-height: 1.65;
  color: #475a7f;
}

@media (max-width: 991px) {
  .feature-block {
    padding: 40px 0;
  }

  .feature-image-wrap {
    max-width: none;
  }

  .feature-title {
    font-size: 1.25rem;
  }

  .market-visual__title {
    font-size: 20px;
  }
}

@media (max-width: 575px) {
  .feature-title {
    font-size: 1.15rem;
  }
}
</style>
